<script setup lang="ts">
import { useEvents } from '~/composables/events/useEvents';
import { useNavigationStore } from '~/store/navigationStore';
import { usePageSectionsStore } from '~/store/pageSectionStore';

defineProps<{
	isVisible: boolean;
}>();

const pageSectionsStore = usePageSectionsStore();
const navigationStore = useNavigationStore();
const contextualHeaderDivRef = ref<HTMLDivElement | null>(null);
const tabClicked = useEventBus<string>('tab-clicked');

function scrollToElement(reference: Element | null, offset: number) {
	if (!reference) return;

	const elementPosition =
		(reference.getBoundingClientRect().top || 0) + window.scrollY - offset;

	pageSectionsStore.setAutomaticScrollingInteracting(true);
	window.scrollTo({ top: elementPosition - 50, behavior: 'smooth' });

	setTimeout(
		() => pageSectionsStore.setAutomaticScrollingInteracting(false),
		2000
	);
}

const handleTabClick = (tabId: string) => {
	pageSectionsStore.setActiveTabId(tabId);

	const currentReference = pageSectionsStore.getSectionRef(tabId);

	if (!currentReference) return;

	const isScrollingDown =
		Math.sign(currentReference.getBoundingClientRect().top || 0) === 1;
	const offset = isScrollingDown ? 50 : 115;

	scrollToElement(currentReference, offset);

	tabClicked.emit(tabId);
};

const isActiveTab = (tabId: string) => tabId === pageSectionsStore.activeTabId;

watchThrottled(
	() => pageSectionsStore.activeTabId,
	newTabId => updateActiveTab(newTabId),
	{
		throttle: 100,
		immediate: true
	}
);

function scrollToStart(): void {
	if (!contextualHeaderDivRef.value) return;
	contextualHeaderDivRef.value.scrollTo({ left: 0, behavior: 'smooth' });
}

function updateActiveTab(newTabId: string): void {
	if (!contextualHeaderDivRef.value) return;

	const activeTabElement = contextualHeaderDivRef.value.querySelector(
		`div[data-tab-id="${newTabId}"]`
	);

	if (activeTabElement) {
		scrollToActiveTab(activeTabElement, newTabId);
	} else {
		scrollToStart();
	}
}

function isFirstTab(
	tabElement: Element,
	allTabElements: NodeListOf<Element>
): boolean {
	return tabElement === allTabElements[0];
}

/**
 * Calculates the visibility state of a tab element within its container
 * @param tabRect - The bounding rectangle of the tab element
 * @param containerRect - The bounding rectangle of the container
 * @returns Object containing visibility information
 */
function calculateVisibility(
	tabRect: DOMRect,
	containerRect: DOMRect
): {
	isFullyVisible: boolean;
	visibleWidth: number;
	isLeftOverflow: boolean;
	isRightOverflow: boolean;
} {
	const visibleLeft = Math.max(tabRect.left, containerRect.left);
	const visibleRight = Math.min(tabRect.right, containerRect.right);
	const visibleWidth = Math.max(0, visibleRight - visibleLeft);
	const isFullyVisible = visibleWidth === tabRect.width;
	const isLeftOverflow = tabRect.left < containerRect.left;
	const isRightOverflow = tabRect.right > containerRect.right;

	return { isFullyVisible, visibleWidth, isLeftOverflow, isRightOverflow };
}

/**
 * Scrolls the contextual header to the specified position
 * @param scrollLeft - The position to scroll to
 */
function scrollToPosition(scrollLeft: number): void {
	if (!contextualHeaderDivRef.value) return;

	contextualHeaderDivRef.value.scrollTo({
		left: Math.max(0, scrollLeft),
		behavior: 'smooth'
	});
}

/**
 * Scrolls the active tab into view
 * @param activeTabElement - The active tab element to scroll into view
 * @param newTabId - The ID of the new active tab
 */
function scrollToActiveTab(activeTabElement: Element, newTabId: string): void {
	if (!contextualHeaderDivRef.value) return;

	const allTabElements =
		contextualHeaderDivRef.value.querySelectorAll('[data-tab-id]');

	if (isFirstTab(activeTabElement, allTabElements)) {
		scrollToStart();
		return;
	}

	const containerRect = contextualHeaderDivRef.value?.getBoundingClientRect();
	const tabRect = activeTabElement?.getBoundingClientRect();
	if (!containerRect || !tabRect) return;

	const { isFullyVisible, isLeftOverflow, isRightOverflow } =
		calculateVisibility(tabRect, containerRect);

	if (isFullyVisible) return;

	const currentScrollLeft = contextualHeaderDivRef.value.scrollLeft;
	const tabElement = activeTabElement as HTMLElement;
	const isLastTab =
		activeTabElement === allTabElements[allTabElements.length - 1];
	const PADDING = 50;

	const getScrollPosition = (): number => {
		if (isLastTab) {
			return (
				tabElement.offsetLeft - containerRect.width + tabRect.width + PADDING
			);
		}

		if (isLeftOverflow) {
			return currentScrollLeft - (containerRect.left - tabRect.left) - PADDING;
		}

		if (isRightOverflow) {
			return (
				currentScrollLeft + (tabRect.right - containerRect.right) + PADDING
			);
		}

		return currentScrollLeft;
	};

	scrollToPosition(getScrollPosition());
}
</script>

<template>
	<nav
		class="flex size-full items-center overflow-hidden bg-primary transition-all duration-300 ease-in md:max-h-none"
		:class="{
			'!h-0 !p-0 !transition-none': !isVisible
		}"
	>
		<div
			ref="contextualHeaderDivRef"
			class="no-scrollbar flex w-full items-center justify-start gap-5 overflow-auto px-5 pt-3 md:pt-0 xl:gap-6 xl:pl-12"
		>
			<div
				v-for="link in pageSectionsStore.contextualHeaderLinks"
				:key="`contextual-header-link: ${link.label}`"
				:data-tab-id="link.route"
				class="flex items-center"
			>
				<Button
					:variant="isActiveTab(link.route) ? 'primary' : 'secondary'"
					class="flex w-full select-none justify-center gap-3 self-center border-0 border-b-[3px] !border-transparent !bg-transparent !px-0 py-0 pb-3 text-base text-white !ring-0 md:py-4"
					:class="[
						isActiveTab(link.route)
							? '!border-beige'
							: 'border-white-dark-4 text-white hover:border-primary',
						{ 'pb-3': !navigationStore.isHeaderVisible }
					]"
					@click="handleTabClick(link.route)"
				>
					<span>
						{{ link.label }}
					</span>
				</Button>
			</div>
		</div>
	</nav>
</template>

<style scoped></style>
